import React from 'react';
import {
  Box,
  Typography,
  Divider,
  IconButton,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AssignmentIcon from '@mui/icons-material/Assignment';
import WarningIcon from '@mui/icons-material/Warning';
import CakeIcon from '@mui/icons-material/Cake';
import PaymentIcon from '@mui/icons-material/Payment';
import DescriptionIcon from '@mui/icons-material/Description';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import FavoriteIcon from '@mui/icons-material/Favorite';

/**
 * Pre-session summary view component.
 * Shows friendly encouragement FIRST, then extended insights sections conditionally.
 */
function PreSessionSummaryView({ data, onBack, onClose, isMobile, navigate }) {
  const extendedInsights = data.extended_insights || {};
  const adminReminders = extendedInsights.admin_reminders || {};

  return (
    <Box sx={{ maxHeight: isMobile ? '85vh' : 450, overflow: 'auto' }}>
      {/* Header */}
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        borderBottom: 1,
        borderColor: 'divider',
        position: 'sticky',
        top: 0,
        bgcolor: 'background.paper',
        zIndex: 1,
      }}>
        <IconButton size="small" onClick={onBack} aria-label="Back">
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography variant="h6" fontWeight={600} sx={{ flex: 1 }}>
          Pre-Session Summary
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ p: 2 }}>
        {/* SESSION GIST - Provider-only reminder (preferred) or fallback to note summary */}
        {/* Session Gist is NOT part of the clinical note - just a quick recap for the provider */}
        {(data.session_gist || data.last_session_summary) && (
          <Box sx={{ 
            mb: 2, 
            p: 1.5, 
            bgcolor: data.session_gist ? 'warning.light' : 'primary.light', 
            borderRadius: 1,
            border: 1,
            borderColor: data.session_gist ? 'warning.main' : 'primary.main',
            opacity: 0.9,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <FavoriteIcon fontSize="small" color={data.session_gist ? 'warning' : 'primary'} sx={{ mt: 0.25 }} />
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, fontWeight: 600 }}>
                  {data.session_gist ? "Last session's gist" : 'Last session summary'}
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {data.session_gist || data.last_session_summary}
                </Typography>
                {data.last_session_date && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Last session: {formatDate(data.last_session_date)}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {/* Anniversary Marker (6A) - Relationship milestone */}
        {data.anniversary_marker && (
          <Box sx={{ 
            mb: 2, 
            p: 1.5, 
            bgcolor: 'info.50', 
            borderRadius: 1,
            border: 1,
            borderColor: 'info.100',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FavoriteIcon fontSize="small" color="info" />
              <Typography variant="body2">
                {data.anniversary_marker.message}
              </Typography>
            </Box>
          </Box>
        )}

        {/* (A) Chart changes since you last saw them */}
        {extendedInsights.chart_changes?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Chart changes since you last saw them
            </Typography>
            <List dense disablePadding>
              {extendedInsights.chart_changes.map((change, idx) => (
                <ListItem key={idx} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <AssignmentIcon fontSize="small" color="info" />
                  </ListItemIcon>
                  <ListItemText
                    primary={change}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* (B) Things you wanted to follow up on today */}
        {extendedInsights.followups_from_last_session?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Things you wanted to follow up on today
            </Typography>
            <List dense disablePadding>
              {extendedInsights.followups_from_last_session.map((followup, idx) => (
                <ListItem key={idx} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <EventRepeatIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={followup}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* (C) Risk watch (since last session) */}
        {extendedInsights.risk_watch?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Risk watch (since last session)
            </Typography>
            <List dense disablePadding>
              {extendedInsights.risk_watch.map((item, idx) => (
                <ListItem key={idx} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <WarningIcon fontSize="small" color="warning" />
                  </ListItemIcon>
                  <ListItemText
                    primary={item}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* (D) Admin & paperwork reminders */}
        <AdminRemindersSection adminReminders={adminReminders} />

        <Divider sx={{ my: 2 }} />

        {/* (E) Since last session (messages, tracking, questionnaires) */}
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Since last session
        </Typography>

        {/* Mood Changes */}
        {data.mood_changes && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="body2">
              {data.mood_changes.trend === 'improving' && 'Mood has been improving'}
              {data.mood_changes.trend === 'declining' && 'Mood has been declining'}
              {data.mood_changes.trend === 'stable' && 'Mood has been stable'}
              {data.mood_changes.average && ` (avg: ${data.mood_changes.average}/10)`}
            </Typography>
          </Box>
        )}

        {/* Sleep Stats */}
        {data.sleep_stats && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="body2">
              {data.sleep_stats.average_hours && `Sleep avg: ${data.sleep_stats.average_hours} hours`}
              {data.sleep_stats.average_quality && ` | Quality: ${data.sleep_stats.average_quality}/10`}
            </Typography>
          </Box>
        )}

        {/* Recent Messages */}
        {data.messages?.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              {data.messages_count} message{data.messages_count !== 1 ? 's' : ''} since last session
            </Typography>
          </Box>
        )}

        {/* Questionnaires */}
        {data.questionnaires?.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              {data.questionnaires_count} form{data.questionnaires_count !== 1 ? 's' : ''} completed
            </Typography>
          </Box>
        )}

        {/* (F) Quick links */}
        {data.links && (
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {data.links.patient_chart && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  navigate(data.links.patient_chart);
                  onClose();
                }}
              >
                View Chart
              </Button>
            )}
            {data.links.messages && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  navigate(data.links.messages);
                  onClose();
                }}
              >
                Messages
              </Button>
            )}
            {data.links.tracking && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  navigate(data.links.tracking);
                  onClose();
                }}
              >
                Health Tracking
              </Button>
            )}
            {data.links.questionnaires && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  navigate(data.links.questionnaires);
                  onClose();
                }}
              >
                Forms
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

/**
 * Admin reminders section component.
 * Shows billing, ROI, birthday, turning 18, follow-up, and paperwork reminders.
 */
function AdminRemindersSection({ adminReminders }) {
  if (!adminReminders) return null;

  const reminders = [];

  // Billing status
  const billing = adminReminders.billing || {};
  if (billing.status === 'payment_expired') {
    reminders.push({ icon: <PaymentIcon fontSize="small" color="error" />, text: 'Payment method on file is expired.' });
  } else if (billing.status === 'payment_missing') {
    reminders.push({ icon: <PaymentIcon fontSize="small" color="error" />, text: 'There is no payment method on file.' });
  } else if (billing.status === 'payment_expiring_soon') {
    reminders.push({ icon: <PaymentIcon fontSize="small" color="warning" />, text: `Payment method on file expires in ${billing.days_until_expiration} days.` });
  } else if (billing.status === 'overdue_balance') {
    reminders.push({ icon: <PaymentIcon fontSize="small" color="error" />, text: `Outstanding balance: $${billing.overdue_amount}.` });
  }

  // Billing confidence (positive message)
  const billingConfidence = adminReminders.billing_confidence || {};
  if (billingConfidence.all_clear && billing.status === 'none') {
    reminders.push({ icon: <CheckCircleIcon fontSize="small" color="success" />, text: 'Billing looks good for this client today.' });
  }

  // ROI status
  const roi = adminReminders.roi || {};
  if (roi.status === 'expiring_soon') {
    reminders.push({ icon: <DescriptionIcon fontSize="small" color="warning" />, text: `A release of information on file expires in ${roi.days_until_expiration} days.` });
  } else if (roi.status === 'expired') {
    reminders.push({ icon: <DescriptionIcon fontSize="small" color="error" />, text: 'A release of information has expired and may need renewal.' });
  }

  // Turning 18
  const turning18 = adminReminders.turning_18 || {};
  if (turning18.status === 'upcoming_18') {
    reminders.push({ icon: <CakeIcon fontSize="small" color="info" />, text: `Client turns 18 in ${turning18.days_until_18} days — updated adult paperwork may be needed.` });
  }

  // Birthday
  const birthday = adminReminders.birthday || {};
  if (birthday.status === 'upcoming') {
    reminders.push({ icon: <CakeIcon fontSize="small" color="primary" />, text: `Their birthday is in ${birthday.days_until} days.` });
  } else if (birthday.status === 'recent') {
    reminders.push({ icon: <CakeIcon fontSize="small" color="primary" />, text: `Their birthday was ${birthday.days_since} days ago — you may want to wish them a belated happy birthday.` });
  }

  // Follow-up
  const followUp = adminReminders.follow_up || {};
  if (followUp.status === 'no_follow_up') {
    reminders.push({ icon: <ScheduleIcon fontSize="small" color="warning" />, text: "There's no follow-up appointment scheduled yet." });
  } else if (followUp.status === 'has_follow_up' && followUp.next_appointment_date) {
    reminders.push({ icon: <ScheduleIcon fontSize="small" color="success" />, text: `Next follow-up booked for ${formatDate(followUp.next_appointment_date)}.` });
  }

  // Pending paperwork
  const paperwork = adminReminders.paperwork || {};
  if (paperwork.pending_forms?.length > 0) {
    paperwork.pending_forms.forEach(form => {
      reminders.push({ icon: <DescriptionIcon fontSize="small" color="warning" />, text: `Pending form: ${form}.` });
    });
  }

  if (reminders.length === 0) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Admin & paperwork reminders
      </Typography>
      <List dense disablePadding>
        {reminders.map((reminder, idx) => (
          <ListItem key={idx} disablePadding sx={{ mb: 0.5 }}>
            <ListItemIcon sx={{ minWidth: 28 }}>
              {reminder.icon}
            </ListItemIcon>
            <ListItemText
              primary={reminder.text}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
}

// Helper function
function formatDate(isoString) {
  if (!isoString) return '';
  const date = new Date(isoString);
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export default PreSessionSummaryView;
